import { CornerSize } from '../types';
import { Island } from '../island.class';
import { nearestSixteenth } from '../../../helpers';


// The front panel structural width is the fixed anchor for the V-shape polygon.
// Back panel widths absorb the irrational √2 remainder, rounded up to the nearest 1/16".
const TARGET_FRONT_PANEL_WIDTH = 36;

export const setSizeCornerPanelsCfn = (island: Island): void => {
	try {
		const size = island.size as CornerSize;
		const oldPanelHeight = island.panels.children[0]?.size.height ?? size.height;
		const frontPanel = island.panels.getFrontPanel();
		const leftSidePanel = island.panels.findById(island.frontPanel.connections.left.targetPanelId);
		const leftBackPanel = island.panels.findById(leftSidePanel.connections.left.targetPanelId);
		const rightSidePanel = island.panels.findById(island.frontPanel.connections.right.targetPanelId);
		const rightBackPanel = island.panels.findById(rightSidePanel.connections.right.targetPanelId);
		const frontThickness = island.getThicknessForRole('front');
		const leftThickness = island.getThicknessForRole('left');
		const rightThickness = island.getThicknessForRole('right');
		const backLeftThickness = island.getThicknessForRole('backLeft');
		const backRightThickness = island.getThicknessForRole('backRight');

		// Geometric derivation: front panel width is the fixed anchor (TARGET_FRONT_PANEL_WIDTH).
		// Back panel widths absorb the irrational √2 remainder so the front stays exact.
		// Formula: ea = depth + (frontWidth / √2), inverted from bc = (ea − depth) × √2.
		const bc = TARGET_FRONT_PANEL_WIDTH;
		const ea = size.depth.left + (bc / Math.SQRT2);
		const ed = ea - backLeftThickness;
		const leftSideWidth = size.depth.left - frontThickness - backLeftThickness;
		const rightSideWidth = size.depth.right - frontThickness - backRightThickness;

		// 1. Front panel — exact rational width
		frontPanel.size = {
			height: size.height,
			width: bc,
			depth: frontThickness
		};

		// 2. Side panels — exact rational widths
		leftSidePanel.size = {
			height: size.height,
			width: leftSideWidth,
			depth: leftThickness
		};
		rightSidePanel.size = {
			height: size.height,
			width: rightSideWidth,
			depth: rightThickness
		};

		// 3. Back panels — absorb the √2 remainder, rounded up to nearest 1/16"
		leftBackPanel.size = {
			height: size.height,
			width: nearestSixteenth(ea, true),
			depth: backLeftThickness
		};
		rightBackPanel.size = {
			height: size.height,
			width: nearestSixteenth(ed, true),
			depth: backRightThickness
		};

		// Shift bottom-only items by the same amount the bottom edge moved
		const heightDelta = (size.height - oldPanelHeight) / 2;
		if (island.initialized && heightDelta !== 0) {
			island.panels.children.forEach((p) => {
				p.panelItemGroups.children.forEach((group) => {
					group.items.children.forEach((pi) => {
						if (pi.boundBottom && !pi.boundTop && !pi.fullHeight) {
							pi.center.y += heightDelta;
						}
					});
				});
			});
		}
	} catch (error) {
		console.error(`setSizeCornerPanelsCfn:Error calculating corner island panel sizes for ${island.sku}:`, error);
	}
}
